import { ReactNode } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";

type AdminGateProps = {
  children: ReactNode;
  /** When true, requires super_admin role (admin alone is not enough). */
  requireSuperAdmin?: boolean;
  /**
   * Where to redirect signed-in non-admins. Defaults to rendering an
   * inline "no access" card; pass a path to fall back to a redirect instead.
   */
  redirectTo?: string;
  /** Optional custom loading fallback. Defaults to a generic skeleton. */
  fallback?: ReactNode;
};

const DefaultSkeleton = () => (
  <div className="space-y-6">
    <Skeleton className="h-9 w-48" />
    <Skeleton className="h-10 w-80" />
    <Card className="shadow-soft">
      <CardHeader><Skeleton className="h-6 w-48" /></CardHeader>
      <CardContent className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
      </CardContent>
    </Card>
  </div>
);

const NoAccessCard = ({ requireSuperAdmin }: { requireSuperAdmin: boolean }) => (
  <div className="mx-auto max-w-md py-16">
    <Card className="shadow-soft">
      <CardHeader className="flex flex-row items-center gap-3">
        <ShieldAlert className="h-5 w-5 text-muted-foreground" aria-hidden />
        <CardTitle className="text-lg">You don't have access to this page</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {requireSuperAdmin
            ? "This area is restricted to super-admin accounts."
            : "This area is restricted to admin accounts."}
        </p>
        <Button asChild variant="outline" size="sm">
          <Link to="/dashboard">Back to dashboard</Link>
        </Button>
      </CardContent>
    </Card>
  </div>
);

/**
 * Single source of truth for admin/super-admin route gating.
 * - Shows a loading fallback while auth + role state resolves.
 * - Signed-out users are redirected to /auth with a redirect-back param.
 * - Signed-in non-admins see an inline "no access" card (or are redirected
 *   if `redirectTo` is set).
 * - Use `requireSuperAdmin` for super-admin-only routes.
 */
export const AdminGate = ({
  children,
  requireSuperAdmin = false,
  redirectTo,
  fallback,
}: AdminGateProps) => {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, isSuperAdmin, loading: roleLoading } = useUserRole();
  const location = useLocation();

  if (authLoading || roleLoading) return <>{fallback ?? <DefaultSkeleton />}</>;

  // Signed-out users → bounce to /auth, keeping their target so they come
  // back here after signing in.
  if (!user) {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth?redirect=${redirect}`} replace />;
  }

  const authorized = requireSuperAdmin ? isSuperAdmin : isAdmin;
  if (!authorized) {
    if (redirectTo) return <Navigate to={redirectTo} replace />;
    return <NoAccessCard requireSuperAdmin={requireSuperAdmin} />;
  }

  return <>{children}</>;
};
